import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { tokenStore, getCurrentSlug } from "@/lib/api";

const AuthCtx = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    if (!tokenStore.getForCurrentContext()) {
      setUser(false);
      setChecking(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      // Purge any token that failed — could be stale from another slug.
      const slug = getCurrentSlug();
      if (slug) tokenStore.clearForSlug(slug);
      else tokenStore.clearAdmin();
      setUser(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password, { slug } = {}) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (!data.access_token) throw new Error("No token returned");
    // Store token in the slot that matches the resulting session:
    //   - platform_admin → admin slot (usable across any /{slug}/* URL)
    //   - tenant user    → keyed by their tenant slug
    if (data.role === "platform_admin") {
      tokenStore.setAdmin(data.access_token);
    } else if (slug) {
      // If the login form was scoped to a slug, and the user IS a member of it,
      // stash it under that slug. If mismatch, still store under user's real slug
      // (fetched from tenant record) so the next call to /me works — but the
      // caller (Login page) is responsible for redirecting them appropriately.
      tokenStore.setForSlug(slug, data.access_token);
    } else if (data.tenant_id) {
      // Login was to universal /login — look up the user's tenant slug from
      // their JWT/user object so we can key the token correctly. We fetch the
      // tenant to get its slug. This request goes out immediately after login.
      try {
        const { data: t } = await api.get(`/tenants/current`, {
          headers: { Authorization: `Bearer ${data.access_token}` },
        });
        if (t?.slug) tokenStore.setForSlug(t.slug, data.access_token);
      } catch { /* non-fatal */ }
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    const slug = getCurrentSlug();
    if (slug) tokenStore.clearForSlug(slug);
    tokenStore.clearAdmin();
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, checking, login, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

// Legacy → canonical role aliases (2026-08-12). Kept in lockstep with the
// same map in backend/auth.py::_ROLE_ALIASES. If you edit one, edit both —
// mismatch causes exactly the class of bug this map was introduced to fix
// (Juliana's CSR-cancel button, where her prod user had the legacy
// `dispatch_csr` role but every frontend check named only `call_center`).
const ROLE_ALIASES = {
  dispatch_csr: "call_center",
  gm_ops: "administrative",
  kpi_viewer: "technician",
};

// Normalize a user role string: trim whitespace, lowercase, resolve legacy
// alias. Callers should route every `user.role` comparison through this.
export function resolveRole(rawRole) {
  if (rawRole == null) return null;
  const r = String(rawRole).trim().toLowerCase();
  return ROLE_ALIASES[r] || r;
}

// Convenience: is this user's canonical role in the given allowed set?
// Use this everywhere a component gates UI on role — never do a raw
// `.includes(user.role)` against a canonical-only list.
export function hasRole(user, ...allowed) {
  if (!user) return false;
  return roleAllows(user.role, allowed);
}

// CC-261 — mirrors backend auth.role_allows: platform_admin passes everything; the tenant
// Administrator ("administrative") passes any check that admits at least one non-platform role.
export function roleAllows(rawRole, allowed) {
  const r = resolveRole(rawRole);
  const allowedSet = new Set((allowed || []).map(resolveRole));
  if (r === "platform_admin" || allowedSet.has(r)) return true;
  return r === "administrative" && [...allowedSet].some((x) => x !== "platform_admin");
}
